import { JoinGate, AdmissionRequests } from '../components/JoinGate.js';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type {
  ControlMode,
  DrawStroke,
  FramePoint,
  RoomSettings,
  VideoSource,
} from '@kinop/shared';
import {
  DEFAULT_ROOM_SETTINGS,
  checkMediaUrl,
  detectProvider,
  detectNativeProvider,
  detectStreamFormat,
  hasSpoiler,
} from '@kinop/shared';
import { EMPTY_ROOM_STATE, RoomConnection } from '@kinop/shared/client';
import {
  bumpStats,
  getAvatar,
  getClientId,
  getHostToken,
  getNickname,
  rememberRoom,
  setNickname,
} from '../lib/storage.js';
import { getTelegramToken } from '../lib/api.js';
import { VideoPlayer } from '../components/VideoPlayer.js';
import { Chat } from '../components/Chat.js';
import { ParticipantList } from '../components/ParticipantList.js';
import { WatcherStrip } from '../components/WatcherStrip.js';
import { SourcePanel } from '../components/SourcePanel.js';
import { AppLink } from '../components/AppLink.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';

interface RoomPageProps {
  code: string;
  onNavigate: (path: string) => void;
}

type SidebarTab = 'chat' | 'people' | 'source';

const TAB_ORDER: SidebarTab[] = ['chat', 'people', 'source'];

/** Числа в интерфейсе форматирует система: разряды и минус зависят от языка. */
const numberFormat = new Intl.NumberFormat('ru-RU', {
  maximumFractionDigits: 0,
});

export function RoomPage({ code, onNavigate }: RoomPageProps): JSX.Element {
  const [nickname, setNicknameState] = useState(getNickname);
  // Пока имя не введено, соединение не открываем: иначе участник
  // появился бы в комнате безымянным и тут же сменил имя.
  const [hasEntered, setHasEntered] = useState(() => !!getHostToken(code) && getNickname().trim().length > 1);
  const [admissionToken, setAdmissionToken] = useState<string | null>(null);
  const enter = useCallback((name: string, token: string | null) => { setNicknameState(name); setAdmissionToken(token); setHasEntered(true); }, []);
  const [tab, setTab] = useState<SidebarTab>('chat');
  const [copied, setCopied] = useState(false);
  const tablistRef = useRef<HTMLElement>(null);

  const connection = useMemo(
    () =>
      hasEntered
        ? new RoomConnection({
            roomCode: code,
            admissionToken,
            nickname: nickname.trim() || 'Гость',
            clientId: getClientId(),
            hostToken: getHostToken(code),
            authToken: getTelegramToken(),
            avatar: getAvatar(),
          })
        : null,
    // Пересоздание соединения при смене имени не нужно — оно обновляется на месте.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, hasEntered],
  );

  useEffect(() => {
    if (!connection) return;
    connection.connect();
    return () => connection.dispose();
  }, [connection]);

  const state = useSyncExternalStore(
    connection?.subscribe ?? (() => () => undefined),
    connection?.getSnapshot ?? (() => EMPTY_ROOM_STATE),
    () => EMPTY_ROOM_STATE,
  );

  const serverNow = useCallback(
    () => connection?.serverNow() ?? Date.now(),
    [connection],
  );

  const handleControl = useCallback(
    (action: 'play' | 'pause' | 'seek' | 'rate', positionMs: number, rate?: number) => {
      connection?.sendControl(action, positionMs, rate);
    },
    [connection],
  );

  const handleDuration = useCallback(
    (ms: number) => connection?.reportDuration(ms),
    [connection],
  );

  const handlePresence = useCallback(
    (presence: {
      positionMs: number;
      isBuffering: boolean;
      localPaused: boolean;
      adPlaying: boolean;
    }) => connection?.reportPresence(presence),
    [connection],
  );

  const handleSource = useCallback(
    (source: VideoSource) => {
      connection?.setSource(source);
      setTab('chat');
    },
    [connection],
  );

  /*
   * Ссылка на видео, переданная с экрана создания.
   *
   * Источник живёт в комнате, а не в форме, поэтому поставить его можно
   * только после входа. Делаем это один раз и сразу убираем параметр из
   * адреса: иначе обновление страницы возвращало бы старое видео поверх
   * того, которое комната успела выбрать.
   */
  const pendingSourceRef = useRef<string | null>(null);
  if (pendingSourceRef.current === null) {
    pendingSourceRef.current = new URLSearchParams(window.location.search).get('v') ?? '';
  }

  useEffect(() => {
    const url = pendingSourceRef.current;
    if (!url || state.status !== 'joined' || state.room?.source) return;
    pendingSourceRef.current = '';
    window.history.replaceState({}, '', window.location.pathname);

    const check = checkMediaUrl(url, window.location.protocol);
    if (!check.ok || !detectNativeProvider(url)) return;

    connection?.setSource({
      kind: 'url',
      url,
      format: detectStreamFormat(url),
      title: detectProvider(url)?.label ?? 'Видео по ссылке',
      durationMs: null,
    });
  }, [connection, state.status, state.room?.source]);

  const handleControlMode = useCallback(
    (mode: ControlMode) => connection?.setControlMode(mode),
    [connection],
  );

  const handleSettings = useCallback(
    (patch: Partial<RoomSettings>) => connection?.setSettings(patch),
    [connection],
  );

  const handleReaction = useCallback(
    (emoji: string, point: FramePoint) => {
      bumpStats({ reactionsSent: 1 });
      connection?.sendReaction(emoji, point);
    },
    [connection],
  );

  const handleStroke = useCallback(
    (stroke: Omit<DrawStroke, 'id' | 'authorClientId' | 'createdAt'>) => {
      bumpStats({ strokesDrawn: 1 });
      connection?.sendStroke(stroke);
    },
    [connection],
  );

  const handleUndoStroke = useCallback(() => connection?.undoStroke(), [connection]);
  const handleClearStrokes = useCallback(() => connection?.clearStrokes(), [connection]);

  const settings = state.room?.settings ?? DEFAULT_ROOM_SETTINGS;

  /*
   * Растворившиеся реакции и штрихи выбрасываем сами.
   *
   * Сервер чистит свой буфер, но событий об этом не шлёт: сообщение
   * «эта реакция погасла» на каждую стоило бы дороже, чем сравнение
   * времени на месте. Раз в секунду достаточно — след живёт секундами.
   */
  useEffect(() => {
    if (!connection) return;
    const timer = setInterval(() => {
      connection.pruneEphemeral(connection.serverNow(), settings.trailSeconds);
    }, 1000);
    return () => clearInterval(timer);
  }, [connection, settings.trailSeconds]);

  /*
   * Время у экрана и история комнат.
   *
   * Считаем только пока видео действительно идёт: открытая вкладка с
   * паузой — это не просмотр. Записываем раз в пять секунд, чтобы не
   * дёргать хранилище каждую секунду.
   */
  const playbackStatus = state.playback?.status ?? 'paused';
  useEffect(() => {
    if (playbackStatus !== 'playing') return;
    const timer = setInterval(() => bumpStats({ watchSeconds: 5 }), 5000);
    return () => clearInterval(timer);
  }, [playbackStatus]);

  const roomName = state.room?.name;
  const sourceTitle = state.room?.source?.title ?? null;
  const participantCount = state.participants.length;
  useEffect(() => {
    if (state.status !== 'joined' || !roomName) return;
    const hour = new Date().getHours();
    bumpStats({
      firstSeenAt: Date.now(),
      // Комната считается людной один раз — при первом достижении четвёрки.
      crowdedRooms: participantCount === 4 ? 1 : 0,
      // Сеанс, начатый ночью: с полуночи до пяти.
      lateNights: hour >= 0 && hour < 5 && participantCount === 1 ? 1 : 0,
    });
    rememberRoom({
      code,
      name: roomName,
      title: sourceTitle,
      at: Date.now(),
      withWhom: state.participants
        .filter((p) => p.clientId !== state.you?.clientId)
        .map((p) => p.nickname),
    });
    // Зависимости узкие: перезаписывать историю на каждое сообщение в чате
    // и на каждый такт плеера незачем.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, roomName, sourceTitle, participantCount, code]);

  /**
   * Переключить текущее видео на проксирование через сервер.
   *
   * Источник переустанавливается целиком — это тот же путь, что при выборе
   * видео, поэтому смена доедет до всех участников обычным событием.
   */
  const retryViaProxy = useCallback(() => {
    const source = state.room?.source;
    if (!source || source.kind !== 'url') return;
    connection?.setSource({ ...source, proxied: true });
  }, [connection, state.room?.source]);

  const inviteUrl = `${window.location.origin}/r/${code}`;

  const copyInvite = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      bumpStats({ invitesCopied: 1 });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // В незащищённом контексте (http без TLS) буфера обмена нет —
      // показываем ссылку, чтобы её можно было выделить руками.
      window.prompt('Скопируйте ссылку:', inviteUrl);
    }
  }, [inviteUrl]);

  const shareInvite = useCallback(async () => {
    if (!navigator.share) {
      void copyInvite();
      return;
    }
    try {
      await navigator.share({
        title: 'Смотрим вместе',
        text: 'Присоединяйся к просмотру',
        url: inviteUrl,
      });
    } catch {
      /* пользователь закрыл окно «Поделиться» */
    }
  }, [copyInvite, inviteUrl]);

  /**
   * Стрелки внутри набора вкладок.
   *
   * Так работают вкладки везде: Tab уводит из набора наружу, а между
   * вкладками ходят стрелками. Без этого клавиатурному пользователю
   * приходится протабиваться через каждую вкладку по очереди.
   */
  const handleTabKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();

    setTab((current) => {
      const next = TAB_ORDER[(TAB_ORDER.indexOf(current) + delta + TAB_ORDER.length) % TAB_ORDER.length]!;
      // Фокус переезжает вслед за выделением — иначе следующая стрелка
      // отсчитывалась бы от старой вкладки.
      requestAnimationFrame(() => {
        tablistRef.current
          ?.querySelector<HTMLButtonElement>(`#tab-${next}`)
          ?.focus();
      });
      return next;
    });
  }, []);

  // ─────────────────────────── экран входа ───────────────────────────

  if (!hasEntered) return <JoinGate code={code} onEnter={enter} onNavigate={onNavigate} />;

  // ─────────────────────────── ошибка входа ───────────────────────────

  if (state.status === 'error') {
    return (
      <main className="gate">
        <div className="card gate__card">
          <h1 className="card__title">Не получилось войти</h1>
          <p className="error" role="alert">
            {state.error?.message ?? 'Неизвестная ошибка'}
          </p>
          <Button asChild size="lg" className="mt-5 w-full">
            <AppLink href="/" onNavigate={onNavigate}>
              На главную
            </AppLink>
          </Button>
        </div>
      </main>
    );
  }

  const amIHost = state.you?.isHost ?? false;
  const canControl = state.room?.controlMode === 'everyone' || amIHost;
  const isConnecting = state.status !== 'joined';

  return (
    <div className="room" data-hide-ending={settings.hideEnding || undefined}>
      <header className="room__header">
        <AppLink className="room__back" href="/" onNavigate={onNavigate}>
          ← Выйти
        </AppLink>

        <div className="room__title">
          <h1>{state.room?.name ?? 'Комната'}</h1>
          {state.room?.source && <p className="room__now">{state.room.source.title}</p>}
        </div>

        <div className="room__actions">
          {state.pingMs != null && (
            <span className="room__ping" title="Задержка до сервера">
              {numberFormat.format(state.pingMs)}&nbsp;мс
            </span>
          )}
          <Button variant="ghost" onClick={() => void shareInvite()}>
            Пригласить
          </Button>
          {/* Подтверждение живёт отдельно от кнопки: подменять надпись на
              самой кнопке — значит на две секунды забрать у неё название. */}
          <span className="room__copied" role="status" aria-live="polite">
            {copied ? 'Ссылка скопирована' : ''}
          </span>
        </div>
      </header>

      {isConnecting && (
        <div className="room__banner" role="status">
          {state.status === 'reconnecting'
            ? 'Связь потеряна, переподключаемся…'
            : 'Подключаемся к комнате…'}
        </div>
      )}

      <div className="room__body">
        <div className="room__main">
          <VideoPlayer
            online={state.status === 'joined'}
            source={state.room?.source ?? null}
            roomCode={code}
            playback={state.playback}
            playbackRevision={state.playbackRevision}
            serverNow={serverNow}
            clockReady={state.clockReady}
            canControl={canControl}
            amIHost={amIHost}
            settings={settings}
            reactions={state.reactions}
            strokes={state.strokes}
            onControl={handleControl}
            onDuration={handleDuration}
            onPresence={handlePresence}
            pausedBy={state.pausedBy}
            onRetryViaProxy={retryViaProxy}
            onReaction={handleReaction}
            onStroke={handleStroke}
            onUndoStroke={handleUndoStroke}
            onClearStrokes={handleClearStrokes}
          />

          {/* Пока видео нет, выбор источника — главное действие на экране.
              Но если человек открыл ту же панель в боковой колонке,
              показывать её дважды незачем. */}
          {!state.room?.source && tab !== 'source' && (
            <SourcePanel
              currentSource={null}
              canControl={canControl}
              onSourceSelected={handleSource}
            />
          )}
        </div>

        <aside className="room__sidebar">
          {amIHost && <AdmissionRequests code={code} />}
          <nav
            className="sidebar-tabs"
            role="tablist"
            // Подложка переезжает средствами CSS: положение выводится из
            // выбранной вкладки, без измерений и подписки на размеры.
            data-active={tab}
            ref={tablistRef}
            onKeyDown={handleTabKeyDown}
          >
            {TAB_ORDER.map((name) => (
              <button
                key={name}
                type="button"
                role="tab"
                id={`tab-${name}`}
                aria-selected={tab === name}
                aria-controls={`panel-${name}`}
                // Из набора вкладок Tab уводит наружу: в фокус попадает
                // только выбранная, между вкладками ходят стрелками.
                tabIndex={tab === name ? 0 : -1}
                className={`sidebar-tabs__tab ${tab === name ? 'sidebar-tabs__tab--active' : ''}`}
                onClick={() => setTab(name)}
              >
                {name === 'chat' && 'Чат'}
                {name === 'people' && `Люди (${state.participants.length})`}
                {name === 'source' && 'Видео'}
              </button>
            ))}
          </nav>

          {tab === 'chat' && (
            <div role="tabpanel" id="panel-chat" aria-labelledby="tab-chat" className="sidebar-panel">
              {/* Полоса зрителей над чатом: когда кино встало у одного,
                  остальные видят причину, не переспрашивая. */}
              <WatcherStrip
                participants={state.participants}
                myClientId={state.you?.clientId ?? null}
                roomPaused={state.playback?.status === 'paused'}
              />
              <Chat
                messages={state.messages}
                participants={state.participants}
                myClientId={state.you?.clientId ?? null}
                onSend={(text) => {
                  if (hasSpoiler(text)) bumpStats({ spoilersSent: 1 });
                  connection?.sendChat(text);
                }}
                onSendSticker={(stickerId) => connection?.sendSticker(stickerId)}
              />
            </div>
          )}

          {tab === 'people' && (
            <div role="tabpanel" id="panel-people" aria-labelledby="tab-people" className="sidebar-panel">
              <ParticipantList
                participants={state.participants}
                myClientId={state.you?.clientId ?? null}
                controlMode={state.room?.controlMode ?? 'everyone'}
                settings={settings}
                amIHost={amIHost}
                onChangeControlMode={handleControlMode}
                onChangeSettings={handleSettings}
              />
            </div>
          )}

          {tab === 'source' && (
            <div role="tabpanel" id="panel-source" aria-labelledby="tab-source" className="sidebar-panel">
              <SourcePanel
                currentSource={state.room?.source ?? null}
                canControl={canControl}
                onSourceSelected={handleSource}
              />
            </div>
          )}
        </aside>
      </div>

      {state.error && state.status === 'joined' && (
        <button
          type="button"
          className="room__toast"
          role="alert"
          onClick={() => connection?.clearError()}
        >
          {state.error.message}
          <span className="room__toast-hint">нажмите, чтобы скрыть</span>
        </button>
      )}
    </div>
  );
}
